const base = process.env.NEXT_PUBLIC_API_URL;
type EventName =
  | "page_view"
  | "cta_click"
  | "story_step"
  | "blox_open"
  | "mobile_view"
  | "scroll_depth"
  | "signup_view";
type Event = {
  id: string;
  name: EventName;
  page: "home" | "signup" | "login";
  element?: string;
};
type Context = {
  sessionId: string;
  device: "mobile" | "tablet" | "desktop";
  source?: string;
  campaign?: string;
  referrerHost?: string;
};
let context: Context | null = null;
let contextExpires = 0;
let queue: Event[] = [];
let sending: Promise<void> | null = null;
export function privacySignal() {
  return (
    navigator.doNotTrack === "1" ||
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}
export function analyticsAllowed() {
  try {
    return (
      !privacySignal() && localStorage.getItem("lb-analytics-choice") === "yes"
    );
  } catch {
    return false;
  }
}
export function setAnalyticsChoice(allowed: boolean) {
  try {
    localStorage.setItem("lb-analytics-choice", allowed ? "yes" : "no");
  } catch {
    /* Collection remains disabled when storage is unavailable. */
  }
  if (!allowed) {
    context = null;
    queue = [];
    try {
      sessionStorage.removeItem("lb-analytics-session");
    } catch {
      /* optional storage */
    }
  }
}
export function analyticsSessionId() {
  return analyticsAllowed() && contextExpires > Date.now() ? context?.sessionId : undefined;
}
function loadContext(): Context | null {
  if (!analyticsAllowed()) return null;
  if (context && contextExpires > Date.now()) return context;
  context = null;
  contextExpires = Date.now() + 1800000;
  let sessionId = crypto.randomUUID();
  let savedContext: Context | undefined;
  try {
    const saved = JSON.parse(
      sessionStorage.getItem("lb-analytics-session") ?? "null",
    ) as { id: string; expires: number; context?: Context } | null;
    if (
      saved &&
      saved.expires > Date.now() &&
      /^[a-f0-9-]{36}$/i.test(saved.id)
    ) {
      sessionId = saved.id;
      contextExpires = saved.expires;
      const prior = saved.context;
      if (prior && ["mobile", "tablet", "desktop"].includes(prior.device) &&
          (!prior.source || /^[a-z0-9_-]{1,32}$/i.test(prior.source)) &&
          (!prior.campaign || /^[a-z0-9_-]{1,80}$/i.test(prior.campaign)) &&
          (!prior.referrerHost || /^[a-z0-9.-]{1,100}$/i.test(prior.referrerHost))) {
        savedContext = { sessionId, device: prior.device, source: prior.source, campaign: prior.campaign, referrerHost: prior.referrerHost };
      }
    } else
      sessionStorage.setItem(
        "lb-analytics-session",
        JSON.stringify({ id: sessionId, expires: Date.now() + 1800000 }),
      );
  } catch {
    return null;
  }
  const params = new URLSearchParams(location.search),
    clean = (name: string, max: number) => {
      const value = params.get(name) ?? "";
      return value.length <= max && /^[a-z0-9_-]+$/i.test(value)
        ? value
        : undefined;
    };
  let referrerHost: string | undefined;
  try {
    const host = new URL(document.referrer).hostname;
    if (host.length <= 100 && /^[a-z0-9.-]+$/i.test(host)) referrerHost = host;
  } catch {
    /* direct visit */
  }
  context = savedContext ?? {
    sessionId,
    device:
      innerWidth < 768 ? "mobile" : innerWidth < 1100 ? "tablet" : "desktop",
    source: clean("utm_source", 32),
    campaign: clean("utm_campaign", 80),
    referrerHost,
  };
  try {
    sessionStorage.setItem("lb-analytics-session", JSON.stringify({ id: sessionId, expires: contextExpires, context }));
  } catch { return null; }
  return context;
}
export function collectLandingEvent(
  name: EventName,
  page: Event["page"],
  element?: string,
) {
  if (!loadContext()) return;
  if (queue.length >= 40) return;
  queue.push({
    id: crypto.randomUUID(),
    name,
    page,
    ...(element ? { element } : {}),
  });
}
export async function flushLandingEvents(): Promise<void> {
  if (!analyticsAllowed() || !context || !queue.length) return;
  if (sending) {
    await sending;
    return flushLandingEvents();
  }
  const batch = queue.splice(0, 20),
    snapshot = context;
  const controller = new AbortController(),
    timer = setTimeout(() => controller.abort(), 1500);
  sending = (async () => {
    try {
      const response = await fetch(base + "/api/v1/analytics/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        keepalive: true,
        cache: "no-store",
        signal: controller.signal,
        body: JSON.stringify({ ...snapshot, consent: true, events: batch }),
      });
      if (response.status >= 500 && analyticsAllowed())
        queue = [...batch, ...queue].slice(0, 40);
    } catch {
      /* Optional analytics must never block account creation. */
    } finally {
      clearTimeout(timer);
      sending = null;
    }
  })();
  await sending;
}
